import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { workspaceListResponse, spaceListResponse } from './types'
import { useAuthStore } from '../stores/authStore'

/**
 * The signed-in user's own personal, private Spaces (the sidebar "Spaces"
 * section) — resolved from the shared `/workspaces/mine` + `/workspaces/:id/
 * spaces` caches, so it costs nothing extra wherever the tree already loads.
 *
 * Returns the personal Space objects, their id set (for active-state checks —
 * e.g. is the currently-open Space a personal one?), and the workspace id.
 */
export function usePersonalSpaces() {
  const userId = useAuthStore((s) => s.user?.id)

  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: async () =>
      workspaceListResponse.parse((await api.get('/workspaces/mine')).data),
    staleTime: 30_000,
  })
  const workspaceId = workspacesQuery.data?.data?.[0]?.id ?? null

  const spacesQuery = useQuery({
    queryKey: ['spaces', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async () =>
      spaceListResponse.parse(
        (await api.get(`/workspaces/${workspaceId}/spaces`)).data,
      ),
    staleTime: 30_000,
  })

  const personalSpaces = (spacesQuery.data?.data ?? []).filter(
    (s) =>
      s.isPersonal &&
      !s.isArchived &&
      String(s.ownerUserId ?? '') === String(userId ?? ''),
  )
  const personalSpaceIds = new Set(personalSpaces.map((s) => String(s.id)))

  // The user's OWN archived personal Spaces. Surfaced so the sidebar can offer
  // a Restore path: an archived personal Space otherwise drops off the sidebar
  // (this list filters `!isArchived`) AND is excluded from Space Explorer's
  // "Archived spaces" restore list (that one is department-scoped and skips
  // personal Spaces), leaving no way back from the UI.
  const archivedPersonalSpaces = (spacesQuery.data?.data ?? []).filter(
    (s) =>
      s.isPersonal &&
      s.isArchived &&
      String(s.ownerUserId ?? '') === String(userId ?? ''),
  )

  return {
    workspaceId,
    personalSpaces,
    personalSpaceIds,
    archivedPersonalSpaces,
    isLoading:
      workspacesQuery.isLoading ||
      (Boolean(workspaceId) && spacesQuery.isLoading),
  }
}
